// Global active-call store (Phase G). Module singleton — one active call at a time,
// busy detection, and page-refresh recovery via sessionStorage. No React required;
// components can subscribe via useActiveCall().

import { useEffect, useState } from "react";
import { setTabCallBusy } from "../../../utils/callCoordinator";

const KEY = "connect_active_call";
let active = null; // { meeting_id, session_type, source_type, source_id, call_type, role }
const listeners = new Set();

function emit() {
  // Publish this tab's busy state browser-wide so other tabs do not ring for a second
  // incoming call while this tab is in one (see utils/callCoordinator.js).
  setTabCallBusy(Boolean(active), "connect_call_state");
  listeners.forEach((fn) => fn(active));
}

function persist() {
  try {
    if (active) sessionStorage.setItem(KEY, JSON.stringify(active));
    else sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

export function getActiveCall() {
  return active;
}

export function setActiveCall(call) {
  active = call;
  persist();
  emit();
}

export function clearActiveCall() {
  active = null;
  persist();
  emit();
}

export function isBusy() {
  return !!active;
}

// Recover a persisted call descriptor after a page refresh (Phase G — active call recovery).
export function recoverPersistedCall() {
  try {
    const s = sessionStorage.getItem(KEY);
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
}

export function subscribeActiveCall(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useActiveCall() {
  const [call, setCall] = useState(active);
  useEffect(() => subscribeActiveCall(setCall), []);
  return call;
}
